import { Text, Input, FormControl, Select, FormLabel, Button, Tabs, Tab, TabList, TabPanels, TabPanel, Spinner, Box, Flex } from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { Dropdown, ButtonGroup } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';

import PageBanner from '../../../components/pageBanner/PageBanner';
import PageContainer from '../../../components/pageContainer/PageContainer';
import {
  getAccountUpgradeRequest,
  executeAccountUpgradeRequest
} from '../../../slice/normalUserHomeSlice';
import { UpgradeRequestEnum, AccountTypeEnum } from '../../../utils/enums';
import { BACKEND_URL } from '../../../api/config';

import './NormalUserHome.css';

function NormalUserHome() {
  const dispatch = useDispatch();

  const upgradeAccountRequest = useSelector(
    state => state.normalUserHome.upgradeRequest
  );

  const [accountType, setAccountType] = useState(AccountTypeEnum.NORMAL_USER);
  const [companyName, setCompanyName] = useState('');
  const [emptyCompanyName, setEmptyCompanyName] = useState(false);

  const [sectors, setSectors] = useState([]);
  const [selectedSector, setSelectedSector] = useState('');
  const [emptySector, setEmptySector] = useState(false);

  /* ---------------- Scan History ---------------- */

  const [scanHistory, setScanHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const fetchScanHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await fetch(
        `${BACKEND_URL}/scan-statistics/user-scan-stats`,
        {
          credentials: 'include',
          cache: 'no-store',
        }
      );

      if (!res.ok) throw new Error();

      const data = await res.json();
      setScanHistory(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch scan history', err);
      setScanHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  /* ---------------- Initial Load ---------------- */

  useEffect(() => {
    dispatch(getAccountUpgradeRequest());
  }, [dispatch]);

  /* ---------------- Upgrade Logic ---------------- */

  const handleSelectAccountType = (type) => {
    setAccountType(type);
    setSelectedSector('');
    setCompanyName('');
    setEmptySector(false);
    setEmptyCompanyName(false);
  };

  const handleSubmit = async () => {
    if (accountType === AccountTypeEnum.NORMAL_USER) return;

    if (accountType === AccountTypeEnum.CERTIFICATE_ISSUER && !companyName.trim()) {
      setEmptyCompanyName(true);
      return;
    }

    if (
      accountType === AccountTypeEnum.CERTIFICATE_ISSUER &&
      !selectedSector
    ) {
      setEmptySector(true);
      return;
    }

    setEmptyCompanyName(false);
    setEmptySector(false);

    let sectorName = null;

    if (accountType === AccountTypeEnum.CERTIFICATE_ISSUER) {
      const sector = sectors.find(s => s.sectorCode === selectedSector);
      sectorName = sector?.sectorName || null;
    }

    await dispatch(
      executeAccountUpgradeRequest({
        accountType,
        entityName: companyName || '',
        sector:
          accountType === AccountTypeEnum.CERTIFICATE_ISSUER
            ? selectedSector
            : '',
        sectorName:
          accountType === AccountTypeEnum.CERTIFICATE_ISSUER
            ? sectorName
            : '',
      })
    );
  };

  /* ---------------- UI ---------------- */

  return (
    <PageContainer>
      <PageBanner />

      <Tabs variant="enclosed" mt={6}>
        <TabList>
          <Tab>Account Upgrade</Tab>
          <Tab onClick={fetchScanHistory}>Scan History</Tab>
        </TabList>

        <TabPanels>

          {/* ---------------- UPGRADE TAB ---------------- */}

          <TabPanel>

            {(upgradeAccountRequest === UpgradeRequestEnum.NOT_FOUND ||
              upgradeAccountRequest === UpgradeRequestEnum.DENIED) && (
              <div className="account-upgrade-container">

                <Text className="account-type-title">
                  Your account is currently a Normal User
                </Text>

                <div className="upgrade-form-container">
                  <Dropdown
                    as={ButtonGroup}
                    onSelect={handleSelectAccountType}
                    className="full-width-dropdown"
                  >
                    <Button variant="success" className="dropdown-button">
                      {accountType === AccountTypeEnum.NORMAL_USER
                        ? 'Choose account type'
                        : `Upgrade to ${accountType}`}
                    </Button>

                    <Dropdown.Toggle split variant="success" />

                    <Dropdown.Menu>
                      <Dropdown.Item eventKey={AccountTypeEnum.CERTIFICATE_ISSUER}>
                        Certificate Issuer
                      </Dropdown.Item>
                      <Dropdown.Item eventKey={AccountTypeEnum.MIDDLE_MAN}>
                        Middle Man
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>

                {accountType === AccountTypeEnum.CERTIFICATE_ISSUER && (
                  <>
                    <FormControl isRequired mt={4}>
                      <FormLabel>Company Name</FormLabel>
                      <Input
                        value={companyName}
                        onChange={e => {
                          setCompanyName(e.target.value);
                          setEmptyCompanyName(false);
                        }}
                        placeholder="Enter Company Name"
                      />
                    </FormControl>

                    <FormControl isRequired mt={4}>
                      <FormLabel>Sector</FormLabel>
                      <Select
                        placeholder="Select Sector"
                        value={selectedSector}
                        onChange={e => {
                          setSelectedSector(e.target.value);
                          setEmptySector(false);
                        }}
                      >
                        {sectors.map(sector => (
                          <option
                            key={sector.sectorCode}
                            value={sector.sectorCode}
                          >
                            {sector.sectorName}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                  </>
                )}

                {emptyCompanyName && (
                  <Text color="red.500" mt={2}>
                    Company name is required
                  </Text>
                )}

                {emptySector && (
                  <Text color="red.500" mt={2}>
                    Sector is required
                  </Text>
                )}

                <Button
                  colorScheme="green"
                  width="100%"
                  mt={6}
                  onClick={handleSubmit}
                  isDisabled={accountType === AccountTypeEnum.NORMAL_USER}
                >
                  Submit Upgrade Request
                </Button>
              </div>
            )}

            {upgradeAccountRequest === UpgradeRequestEnum.PROCESSING && (
              <Text fontWeight={600}>
                Your upgrade request is being processed.
              </Text>
            )}

          </TabPanel>

          {/* ---------------- HISTORY TAB ---------------- */}

          <TabPanel>
            {loadingHistory ? (
              <Flex justify="center" py={10}>
                <Spinner />
              </Flex>
            ) : scanHistory.length === 0 ? (
              <Text color="gray.500">No scan history found.</Text>
            ) : (
              (() => {
                const sorted = [...scanHistory].sort(
                  (a, b) => new Date(b.scanTime) - new Date(a.scanTime)
                );

                const grouped = sorted.reduce((acc, scan) => {
                  if (!acc[scan.certificateId]) {
                    acc[scan.certificateId] = [];
                  }
                  acc[scan.certificateId].push(scan);
                  return acc;
                }, {});

                return Object.entries(grouped).map(([certId, scans]) => (
                  <Box
                    key={certId}
                    mb={8}
                    p={5}
                    border="1px solid #e2e8f0"
                    borderRadius="lg"
                    boxShadow="sm"
                    bg="white"
                  >
                    {/* Certificate Header */}
                    <Text fontSize="lg" fontWeight="bold" mb={4}>
                      Certificate: {certId}
                    </Text>

                    {/* Timeline */}
                    <Box borderLeft="3px solid #3182ce" pl={6}>
                      {scans.map((scan, index) => (
                        <Box key={index} mb={6} position="relative">
                          {/* Timeline Dot */}
                          <Box
                            position="absolute"
                            left="-11px"
                            top="5px"
                            width="14px"
                            height="14px"
                            bg="#3182ce"
                            borderRadius="50%"
                          />

                          <Text fontWeight="600">
                            {scan.location || 'Unknown Location'}
                          </Text>

                          <Text fontSize="sm" color="gray.600">
                            {new Date(scan.scanTime).toLocaleString()}
                          </Text>

                          <Text fontSize="sm" mt={1}>
                            <strong>Scanned By:</strong> {scan.accountType}
                          </Text>

                          <Text fontSize="sm">
                            <strong>Scan Type:</strong> {scan.scanType}
                          </Text>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ));
              })()
            )}

          </TabPanel>
        </TabPanels>
      </Tabs>
    </PageContainer>
  );
}

export default NormalUserHome;